import React, { useState, useCallback, useMemo, useContext } from 'react';
import styled from 'styled-components';
import {
  Avatar as BaseAvatar,
  Loader as BaseLoader,
  FormModal as BaseFormModal,
  OrdersTabDetails
} from '@ubeya/shared-web/components';
import { getDirtyFields, splitDirtyArrayToStatus } from '@ubeya/shared/utils/form';
import useToaster from '@ubeya/shared/hooks/useToaster';
import useSupplier from '@ubeya/shared/hooks/account/useSupplier';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import PrivilegesContext from '@ubeya/shared-web/contexts/PrivilegesContext';
import privileges from '@ubeya/shared-web/privileges';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import { setWith } from 'lodash';
import useUser from '@ubeya/shared/hooks/auth/useUser';
import Tabs from './Tabs';
import TabContent from './TabContent';
import Profile from './Profile';
import SupplierInvoices from './Invoices/SupplierInvoices';
import { OrdersTab } from './SupplierCardOrdersTab/OrdersTab';
import { MARK_UP_TYPE_BY_ID, MARK_UP_TYPE_BY_SLUG, MARK_UP_TYPE_LABEL } from './constants';
import SupplierRates from './SupplierRates';
import SupplierPositionsMappingTab from './SupplierPositionsMappingTab';

const FormModal = styled(BaseFormModal)`
  .content {
    overflow: hidden;
  }
`;

const Loader = styled(BaseLoader)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
`;

const OrderTabDetailsContainer = styled.div`
  z-index: 3;
`;

const Avatar = styled(BaseAvatar)`
  margin-right: 10px;
`;
const Container = styled.div`
  display: flex;
  align-items: center;
`;

const formatItemWithWage = (item) => ({
  ...item,
  wage: {
    hourlyWage: item.wage.hourlyWage === '' ? undefined : item.wage.hourlyWage,
    flatWage: item.wage.flatWage === '' ? undefined : item.wage.flatWage,
    flatHours: item.wage.flatHours === '' ? undefined : item.wage.flatHours
  },
  payrollWage: {
    hourlyWage: item.payrollWage.hourlyWage === '' ? undefined : item.payrollWage.hourlyWage,
    flatWage: item.payrollWage.flatWage === '' ? undefined : item.payrollWage.flatWage,
    flatHours: item.payrollWage.flatHours === '' ? undefined : item.payrollWage.flatHours
  }
});

// SEND TO BACKEND WITH WAGE AND PAYROLL WAGE
const formatWagesItemWithWage = (item) => {
  return {
    ...item,
    clientWage: {
      hourlyWage: item.wage.hourlyWage === '' ? undefined : item.wage.hourlyWage,
      flatWage: item.wage.flatWage === '' ? undefined : item.wage.flatWage,
      flatHours: item.wage.flatHours === '' ? undefined : item.wage.flatHours
    },
    wage: {
      hourlyWage: item.payrollWage.hourlyWage === '' ? undefined : item.payrollWage.hourlyWage,
      flatWage: item.payrollWage.flatWage === '' ? undefined : item.payrollWage.flatWage,
      flatHours: item.payrollWage.flatHours === '' ? undefined : item.payrollWage.flatHours
    }
  };
};

const SupplierCard = ({ defaultData, onClose, onConfirm = onClose }) => {
  const { t } = useContext(ConfigContext);
  const { accountId } = useAccount();
  const { isUbeyaAdmin } = useUser();
  const [currentOrder, setCurrentOrder] = useState();

  const supplierId = defaultData?.accountId;
  const [activeTab, setActiveTab] = useState(supplierId ? 'financials' : 'settings');

  const myPrivileges = useContext(PrivilegesContext);

  const { loading, data, updateSupplier } = useSupplier({ supplierId });
  const { addResponseError } = useToaster();

  const initialValues = useMemo(() => {
    const initialData = { ...defaultData, ...data };

    const mappedClientPositionsToSupplierByClientPositionId =
      initialData.mappedClientPositionsToSupplierByType?.byClient || {};

    return {
      ...initialData,

      //manual mapping for Ubeya admin only
      manualClientSupplierPositionMapping: isUbeyaAdmin ? mappedClientPositionsToSupplierByClientPositionId : undefined,

      //supplier positions mapped
      positions: initialData.isClientControlsRates
        ? (initialData.positions || []).filter(({ isMappedToSupplier }) => !!isMappedToSupplier)
        : initialData.positions,

      //supplier positions mapped -> get only client ids
      mappedClientPositionsToSupplierIds: Object.keys(mappedClientPositionsToSupplierByClientPositionId).map(Number),
      markupType: MARK_UP_TYPE_BY_SLUG[initialData.markupType || MARK_UP_TYPE_LABEL.AMOUNT]?.id
    };
  }, [data, defaultData, isUbeyaAdmin]);

  const handleSubmit = useCallback(
    async (values, form) => {
      let supplierValues = values;

      if (supplierId) {
        const { dirtyFields } = form.getState();

        const {
          mappedClientPositionsToSupplierIds: mappedClientPositionsToSupplierIdsIsDirty,
          manualClientSupplierPositionMapping: manualClientSupplierPositionMappingIsDirty,
          positionsWages: positionsIsDirty,
          ...restDirty
        } = dirtyFields;

        const formattedPositions = values.positionsWages
          // IF THERE'S STATUS, THEN IT'S DIRTY
          .filter(({ status }) => !!status)
          .map((positionItem) => {
            const formattedItem = formatItemWithWage(positionItem);
            const wages = (formattedItem.wages || []).map((item) => formatWagesItemWithWage(item));

            const byStatusWages = splitDirtyArrayToStatus('wages', { wages });

            return {
              ...formattedItem,
              wages: {
                added: byStatusWages?.added || [],
                edited: byStatusWages?.edited || [],
                deleted: byStatusWages?.deleted || []
              }
            };
          });

        supplierValues = {
          ...Object.entries(getDirtyFields(values, restDirty)).reduce(
            (carry, [key, value]) => setWith(carry, key, value),
            {}
          ),
          //avoid the added, updated, deleted transformer
          ...(mappedClientPositionsToSupplierIdsIsDirty && {
            mappedClientPositionsToSupplier: values.mappedClientPositionsToSupplierIds
          }),
          ...(positionsIsDirty && { positionsWages: formattedPositions }),

          ...(isUbeyaAdmin &&
            manualClientSupplierPositionMappingIsDirty && {
              manualClientSupplierPositionMapping: Object.entries(values.manualClientSupplierPositionMapping)
                .map(([key, value]) => ({
                  clientPositionId: Number(key),
                  supplierPositionId: value
                }))
                // not as put, but only what's added or updated (not already mapped)
                .filter(({ clientPositionId, supplierPositionId }) => {
                  if (!(values.mappedClientPositionsToSupplierIds || []).includes(clientPositionId)) {
                    //new added
                    return true;
                  }

                  //updated to different value
                  return initialValues.manualClientSupplierPositionMapping[clientPositionId] !== supplierPositionId;
                })
            })
        };
      }

      try {
        const supplierData = await updateSupplier({
          ...supplierValues,
          markupType: supplierValues.markupType ? MARK_UP_TYPE_BY_ID[supplierValues.markupType] : undefined
        });
        onConfirm(supplierData);
      } catch (err) {
        addResponseError(err);
      }
    },
    [
      addResponseError,
      initialValues.manualClientSupplierPositionMapping,
      isUbeyaAdmin,
      onConfirm,
      supplierId,
      updateSupplier
    ]
  );

  const supplierBranchesIds = useMemo(() => (data?.branches || []).map(({ id }) => id), [data?.branches]);

  const allowClientBillingInfo = useMemo(
    () => (supplierId ? myPrivileges[privileges.CLIENT_BILLING] || false : false),
    [supplierId, myPrivileges]
  );

  const editMode = !!supplierId;

  const title = editMode ? (
    <Container>
      <Avatar firstName={initialValues.name} image={initialValues.logo} size="big" />
      <div>{initialValues.name}</div>
    </Container>
  ) : (
    <div>{t('addSupplier')}</div>
  );

  return (
    <FormModal
      initialValues={initialValues}
      onSubmit={handleSubmit}
      onClose={onClose}
      title={title}
      showConfirmButton
      confirmText={!editMode ? 'add' : 'update'}
      isPaddingContent={false}
      sideDetails={
        currentOrder && (
          <OrderTabDetailsContainer>
            <OrdersTabDetails
              order={currentOrder}
              onClose={setCurrentOrder}
              isClient={currentOrder.clientAccountId === accountId}
            />
          </OrderTabDetailsContainer>
        )
      }
      showSideDetailsModal={!!currentOrder}>
      {({ submitting }) => (
        <>
          <Tabs
            activeTab={activeTab}
            editMode={editMode}
            setActiveTab={setActiveTab}
            allowClientBillingInfo={allowClientBillingInfo}
            handleClose={onClose}
            isClientControlsRates={initialValues.isClientControlsRates}
          />

          {loading && <Loader />}

          <TabContent submitting={submitting} activeTab={activeTab}>
            <Profile activeTab="settings" editMode={editMode} />
            <SupplierInvoices
              activeTab="financials"
              supplierId={supplierId}
              supplierName={initialValues.name}
              supplierBranchesIds={supplierBranchesIds}
              isShown={activeTab === 'financials'}
            />

            <SupplierRates activeTab="rates" />

            <SupplierPositionsMappingTab activeTab="positions-mapping" supplierPositions={data?.positions || []} />

            {activeTab === 'orders' && (
              <OrdersTab activeTab="orders" supplierId={supplierId} onOpenOrderDetails={setCurrentOrder} isActive />
            )}
          </TabContent>
        </>
      )}
    </FormModal>
  );
};

export default SupplierCard;
